import React, { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { UserX, UserCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";

export function DeactivateUserButton({ employee, onUpdated }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const isActive = employee.active;

  const apply = async () => {
    setBusy(true); setError("");
    try {
      await api.put(`/admin/users/${employee.id}/active`, { active: !isActive });
      toast.success(isActive ? "Medewerker gedeactiveerd" : "Medewerker geactiveerd");
      setOpen(false); onUpdated();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <AlertDialog open={open} onOpenChange={(v) => { if (!busy) { setOpen(v); if (v) setError(""); } }}>
      <AlertDialogTrigger asChild>
        {isActive ? (
          <Button variant="ghost" size="sm" className="text-amber-700 hover:text-amber-800 hover:bg-amber-50"
            aria-label={`${employee.name} deactiveren`} data-testid={`deactivate-user-${employee.id}`}>
            <UserX className="w-4 h-4 mr-1" />Deactiveren
          </Button>
        ) : (
          <Button variant="ghost" size="sm" className="text-red-700 hover:text-red-800 hover:bg-red-50"
            aria-label={`${employee.name} activeren`} data-testid={`activate-user-${employee.id}`}>
            <UserCheck className="w-4 h-4 mr-1" />Activeren
          </Button>
        )}
      </AlertDialogTrigger>
      <AlertDialogContent className="max-w-lg bg-white dark:bg-slate-900" data-testid="deactivate-user-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{isActive ? "Medewerker deactiveren?" : "Medewerker activeren?"}</AlertDialogTitle>
          <AlertDialogDescription>
            <span className="block font-semibold text-foreground break-words mt-2" data-testid="deactivate-user-identity">{employee.name} · {employee.email}</span>
            {isActive ? (
              <span className="block mt-3">De medewerker kan niet meer inloggen en verschijnt niet langer in nieuwe planningen. <strong>Alle historische diensten, statistieken en registraties blijven bewaard.</strong> Je kunt dit later weer activeren.</span>
            ) : (
              <span className="block mt-3">De medewerker krijgt weer toegang en kan opnieuw worden ingepland.</span>
            )}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {error && <div role="alert" className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-3" data-testid="deactivate-user-error">{error}</div>}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy} data-testid="deactivate-user-cancel">Annuleren</AlertDialogCancel>
          <Button variant={isActive ? "default" : "default"} disabled={busy} onClick={apply} data-testid="deactivate-user-confirm">
            {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {busy ? "Bezig…" : isActive ? "Deactiveren" : "Activeren"}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
